// ---------------------------------------------------------------------------
// THE DATA SURFACE, as sx recipes (DESIGN.md §15): the sheet a primary list
// is, its navy head band, its rows, its footer and the controls of its filter
// grid. A list spreads these on the MUI pieces it already uses — the
// interaction stays MUI's, the look is Universe's.
//
// These sit beside the older src/components/table/tableTokens.js (the indigo
// recipes every other list still wears) and replace them list by list, as the
// migration plan says: touch = migrate.
// ---------------------------------------------------------------------------

import { alpha } from '@mui/material/styles';
import {
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  fieldInkOf,
  fieldLineOf,
  focusRingSx,
  hairlineOf,
  sunkenOf,
  tabularSx
} from './brand';
import { toneTintOf } from './tones';

const isLight = (theme) => theme.palette.mode === 'light';

/** The one sheet a primary list is: paper, a hairline, no shadow. */
export const sheetSx = (theme) => ({
  bgcolor: 'background.paper',
  border: `1px solid ${hairlineOf(theme)}`,
  borderRadius: `${RADIUS.panel}px`,
  boxShadow: 'none',
  overflow: 'visible',
  minWidth: 0
});

/**
 * The navy head band of a PRIMARY surface, in both modes: 36px, white 13/600
 * labels, the sort arrow white at .72, the sorted column in the light blue,
 * rounded 8px ends inset in the sheet.
 */
export const primaryHeadSx = () => ({
  '& .MuiTableCell-head': {
    height: 36,
    py: 0,
    px: 1.5,
    bgcolor: UNIVERSE.navy,
    backgroundImage: 'none',
    color: UNIVERSE.white,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 0,
    border: 0,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:first-of-type': {
      pl: 2.5,
      borderRadius: `${RADIUS.small}px 0 0 ${RADIUS.small}px`,
      boxShadow: 'none'
    },
    '&:last-of-type': {
      pr: 2.5,
      borderRadius: `0 ${RADIUS.small}px ${RADIUS.small}px 0`,
      boxShadow: 'none'
    }
  },
  // the sorted column is said by weight and its arrow, not by a colour (the
  // owner, 07/10/2026: the light blue on "Created" "I don't like it much")
  '& .MuiTableSortLabel-root': {
    color: UNIVERSE.white,
    textTransform: 'none',
    '&:hover': { color: UNIVERSE.white },
    '&.Mui-active': { color: UNIVERSE.white, fontWeight: 700 },
    '&:focus-visible': {
      outline: `2px solid ${UNIVERSE.light}`,
      outlineOffset: 2
    }
  },
  '& .MuiTableSortLabel-icon': {
    color: `${alpha(UNIVERSE.white, 0.72)} !important`,
    width: 16,
    height: 16
  },
  '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
    color: `${UNIVERSE.white} !important`
  }
});

/**
 * The rows: 44px (36 compact), a hairline between them, the accent as a
 * whisper under the pointer, a 2px accent edge and tint on the selected row,
 * the focus ring on the focused one. Figures are tabular.
 */
export const rowsSx = (theme) => ({
  // 52px rows (the owner, 07/10/2026: "a bit more leg space so users do not
  // misclick the wrong project"), 40 compact
  '& .MuiTableCell-body': {
    height: 52,
    py: 1,
    px: 1.5,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    lineHeight: 1.45,
    color: 'text.primary',
    borderBottom: `1px solid ${hairlineOf(theme)}`,
    '&:first-of-type': { pl: 2.5 },
    '&:last-of-type': { pr: 2.5 }
  },
  '& .MuiTableRow-root.is-compact .MuiTableCell-body': { height: 40, py: 0.5 },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 0 },
  '& .MuiTableRow-root': {
    transition: theme.transitions.create('background-color', {
      duration: MOTION.fast
    }),
    '&:hover': { bgcolor: accentTint(theme, 'hover') },
    '&.is-selected': {
      bgcolor: accentTint(theme, 'selected'),
      '& .MuiTableCell-body:first-of-type': {
        boxShadow: `inset 2px 0 0 ${accentOf(theme)}`
      }
    },
    // A table row has no box for a shadow in Chrome; the ring is an outline.
    '&:focus-visible': {
      outline: `2px solid ${accentOf(theme)}`,
      outlineOffset: -2
    }
  },
  '& .MuiTableRow-root.is-context': {
    bgcolor: sunkenOf(theme),
    '&:hover': { bgcolor: sunkenOf(theme) }
  },
  '& .MuiTableRow-root.is-context .MuiTableCell-body': {
    height: 'auto',
    py: 1.5
  },
  '& .MuiTypography-root': { fontFamily: UNIVERSE_FONT },
  '& .is-numeric': tabularSx
});

/** The rows while the next answer loads: dimmed, never unmounted. */
export const busySx = (busy) => ({
  transition: `opacity ${MOTION.standard}ms, filter ${MOTION.standard}ms`,
  ...(busy && { opacity: 0.4, filter: 'saturate(0.6)', pointerEvents: 'none' })
});

/** The 3px accent progress line a reload shows above the head. */
export const progressSx = (theme) => ({
  position: 'absolute',
  top: -2,
  left: 0,
  right: 0,
  height: 3,
  zIndex: 3,
  borderRadius: 0,
  bgcolor: accentTint(theme, 'soft'),
  '& .MuiLinearProgress-bar': { bgcolor: accentOf(theme) }
});

/** The footer row: 48px, a hairline above, the page controls in meta type. */
export const footerSx = (theme) => ({
  minHeight: 48,
  px: 1,
  borderTop: `1px solid ${hairlineOf(theme)}`
});

export const paginationSx = (theme) => ({
  borderTop: 0,
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    height: 48,
    minHeight: 48,
    px: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  '& .MuiTablePagination-spacer': { flex: 'none' },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    m: 0,
    fontFamily: UNIVERSE_FONT,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '24px',
    color: theme.palette.text.secondary,
    ...tabularSx
  },
  '& .MuiTablePagination-input': { m: 0, mr: 1, height: 24 },
  '& .MuiTablePagination-select': {
    fontFamily: UNIVERSE_FONT,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '24px',
    py: 0,
    pl: 0.5,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    '&:focus': { borderRadius: `${RADIUS.control}px` }
  },
  '& .MuiTablePagination-selectIcon': { color: theme.palette.text.secondary },
  '& .MuiTablePagination-actions': {
    m: 0,
    ml: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.25
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    p: 0.75,
    borderRadius: `${RADIUS.button}px`,
    color: theme.palette.text.secondary,
    '&.Mui-disabled': { color: theme.palette.text.disabled },
    '&:hover': { bgcolor: accentTint(theme, 'hover'), color: accentOf(theme) },
    '&:focus-visible': focusRingSx(theme)
  }
});

/**
 * The controls of the filter grid, ONE SHELL for every field (the owner,
 * 07/10/2026: "fields have different borders, and do not have the same
 * height and look"): the field box itself — MUI's input root, and the box a
 * list draws around the antd picker — is 36px, radius 6, the control line on
 * paper, and it alone draws the hover line and the one focus ring. MUI's own
 * outline is not drawn and everything inside the shell is borderless and
 * fills its height, so a text field, a select and an autocomplete come out
 * the same box, to the pixel. Labels sit above the field in quiet meta type.
 * Spread on a Box that wraps the fields.
 */
export const filterControlsSx = (theme) => {
  const accent = accentOf(theme);
  const ring = `0 0 0 3px ${alpha(accent, isLight(theme) ? 0.24 : 0.32)}`;
  const secondary = theme.palette.text.secondary;
  return {
    '& .MuiFormControl-root': { width: 1 },
    // THE SHELL
    '& .MuiInputBase-root': {
      // the shell is the positioned box the glyphs hang in (the theme's
      // autocomplete override lets the adornment escape to the form control)
      position: 'relative',
      boxSizing: 'border-box',
      height: 36,
      pl: '12px !important',
      pr: '12px !important',
      py: '0 !important',
      border: `1px solid ${fieldLineOf(theme)}`,
      borderRadius: `${RADIUS.control}px`,
      bgcolor: 'background.paper',
      color: fieldInkOf(theme),
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1,
      transition: `border-color ${MOTION.fast}ms, box-shadow ${MOTION.fast}ms`,
      // the line warms towards the accent under the pointer, never to black
      '&:hover': { borderColor: alpha(accent, 0.5) },
      '&.Mui-focused': { borderColor: accent, boxShadow: ring },
      '&.Mui-disabled': { opacity: 0.6 }
    },
    '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
    // what is inside fills the shell
    '& .MuiInputBase-input': {
      boxSizing: 'border-box',
      height: 34,
      p: '0 !important',
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '34px',
      color: fieldInkOf(theme)
    },
    '& .MuiInputBase-input::placeholder': {
      color: secondary,
      fontWeight: 400,
      opacity: 1
    },
    // MUI hides a placeholder until its floating label has shrunk; here the
    // label is static above the field, so the placeholder always shows
    '& label[data-shrink=false] + .MuiInputBase-formControl .MuiInputBase-input::placeholder':
      { opacity: '1 !important' },
    '& .MuiInputAdornment-positionStart': { ml: 0, mr: 1, color: secondary },
    '& .MuiInputAdornment-positionEnd': { ml: 0.5, mr: -0.5 },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
      p: 0.5,
      color: secondary,
      '&:hover': { color: accent, bgcolor: 'transparent' }
    },
    // the select: its value line fills the shell, the chevron is the
    // registry's where MUI would draw its own
    // a block, not a flex box: only a block can ellipsise a long value
    // ("To Be Claimed, Claimed, File Preparation…") before the chevron
    // (the owner, 07/10/2026: "truncate … for status because it overlaps")
    '& .MuiSelect-select': {
      boxSizing: 'border-box',
      height: '34px !important',
      minHeight: '0 !important',
      display: 'block',
      lineHeight: '34px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      p: '0 28px 0 0 !important',
      fontWeight: '500 !important',
      color: `${fieldInkOf(theme)} !important`
    },
    '& .MuiSelect-icon': {
      right: 10,
      top: 'calc(50% - 8px)',
      width: 16,
      height: 16,
      color: secondary,
      transition: `transform ${MOTION.fast}ms`
    },
    // the autocomplete: the same shell, its glyphs at the same place
    '& .MuiAutocomplete-inputRoot': {
      p: '0 6px 0 12px !important',
      height: 36,
      flexWrap: 'nowrap !important'
    },
    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      p: '0 !important',
      height: 34,
      minWidth: 0
    },
    // MUI hangs the glyphs from an absolute box whose anchor drifts under
    // the theme; here they are the last item of the shell's own flex row,
    // centred like everything else in it
    '& .MuiAutocomplete-endAdornment': {
      position: 'static !important',
      top: 'auto !important',
      right: 'auto !important',
      transform: 'none !important',
      ml: 'auto',
      display: 'flex',
      alignItems: 'center',
      flex: 'none'
    },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      p: 0.5,
      color: secondary,
      '&:hover': { color: accent, bgcolor: 'transparent' }
    },
    // The MUI label floats inside the field; here it is the quiet label ABOVE
    // it: the field's content is the information, the label only names it.
    '& .MuiInputLabel-root': {
      position: 'static',
      transform: 'none',
      mb: 0.5,
      fontFamily: UNIVERSE_FONT,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.3,
      color: secondary,
      '&.Mui-focused': { color: secondary },
      '&.Mui-disabled': { color: theme.palette.text.disabled }
    }
  };
};

/**
 * A removable applied-filter token, in the same grey as a neutral status
 * label (the owner, 07/10/2026: "the same grey as status, that dark grey
 * doesn't look modern"): 22px, radius 6, the 10% wash and the thin line.
 */
export const appliedChipSx = (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  height: 22,
  pl: 0.875,
  pr: 0.25,
  borderRadius: `${RADIUS.control}px`,
  fontFamily: UNIVERSE_FONT,
  fontSize: 12,
  fontWeight: 500,
  // the whole chip in the neutral label's ink, like "To Be Claimed"
  color: theme.palette.text.secondary,
  bgcolor: toneTintOf(theme, 'neutral', 'label'),
  boxShadow: `inset 0 0 0 1px ${toneTintOf(theme, 'neutral', 'line')}`,
  '& .label': { mr: 0.25 },
  '& .value': { fontWeight: 600 },
  '& .remove': {
    width: 18,
    height: 18,
    p: 0,
    border: 0,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: 'text.secondary',
    bgcolor: 'transparent',
    '&:hover': { color: accentOf(theme) },
    '&:focus-visible': focusRingSx(theme)
  }
});

/**
 * The saved views of a list as TABS (DESIGN.md §15): a 44px row on a hairline
 * baseline, each tab a glyph and a word. The chosen view is lit — its word
 * and glyph in the accent, the glyph the filled one — and its 2px accent
 * underline GLOWS (the owner, 07/10/2026 evening: "the default one should
 * glow"; and, on a row of pills: "these don't look like tabs anymore").
 * Under the pointer a tab's word darkens, its glyph takes the accent and a
 * soft wash rises behind it, rounded at the top like a tab, so the hover is
 * felt before the click. No uppercase, no ripple. The glyph is centred on
 * the word's line box, not hung from the text baseline.
 */
export const textTabsSx = (theme) => {
  const light = isLight(theme);
  const accent = accentOf(theme);
  return {
    minHeight: 44,
    borderBottom: `1px solid ${hairlineOf(theme)}`,
    '& .MuiTabs-flexContainer': { gap: 0.5 },
    // the chosen view's underline is the gradient — the glow drawn as light,
    // not as a shadow (the owner, 07/10/2026: "I don't like the shadow")
    '& .MuiTabs-indicator': {
      height: 2,
      borderRadius: '2px 2px 0 0',
      bgcolor: 'transparent',
      backgroundImage: UNIVERSE.gradient
    },
    // a tab's glyph sits before its word, centred on the word's line
    '& .MuiTab-iconWrapper': {
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 0,
      mr: 0.875,
      mb: 0,
      color: theme.palette.text.secondary,
      transition: `color ${MOTION.standard}ms`
    },
    '& .MuiTab-labelIcon': { minHeight: 44, flexDirection: 'row' },
    '& .MuiTab-root': {
      minHeight: 44,
      minWidth: 0,
      px: 1.25,
      py: 0,
      alignItems: 'center',
      borderRadius: '8px 8px 0 0',
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: 'none',
      color: theme.palette.text.secondary,
      transition: theme.transitions.create(['background-color', 'color'], {
        duration: MOTION.standard,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)'
      }),
      '&:hover': {
        color: theme.palette.text.primary,
        bgcolor: accentTint(theme, 'hover'),
        '& .MuiTab-iconWrapper': { color: accent }
      },
      // the chosen word: the accent on paper, plain white on the night (the
      // light blue reads washed as a word there; it stays on the glyph)
      '&.Mui-selected': {
        color: light ? accent : theme.palette.text.primary,
        fontWeight: 600,
        '& .MuiTab-iconWrapper': { color: accent }
      },
      '&:focus-visible': {
        ...focusRingSx(theme),
        borderRadius: `${RADIUS.small}px`
      },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
    }
  };
};

/**
 * THE GLYPH OF A BLOCK: a rounded tile in the accent on its own wash, before
 * the title of a form section, a report tile or a profile block. It is not
 * the icon tile §10 forbids on a section TITLE of a record composition: it
 * names a form section and a figure, which the product has always drawn this
 * way, and the owner asked for them back on 08/10/2026 ("the page looks bad
 * and lost personality").
 */
export const glyphTileSx = (theme, size = 32) => ({
  width: size,
  height: size,
  flex: 'none',
  borderRadius: `${RADIUS.small}px`,
  display: 'grid',
  placeItems: 'center',
  color: accentOf(theme),
  bgcolor: accentTint(theme, 'soft')
});

/** A floating menu paper: stratum 3, radius 12, rows 36 (DESIGN.md §25). */
export const menuPaperSx = (theme) => ({
  mt: 0.5,
  minWidth: 200,
  p: 0.75,
  borderRadius: `${RADIUS.overlay}px`,
  border: `1px solid ${hairlineOf(theme)}`,
  boxShadow: isLight(theme)
    ? '0 16px 40px -8px rgba(1,38,109,.20)'
    : '0 20px 48px -10px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08)',
  '& .MuiMenuItem-root': {
    minHeight: 36,
    px: 1,
    py: 0.75,
    gap: 1.25,
    borderRadius: `${RADIUS.control}px`,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    fontWeight: 500,
    color: 'text.primary',
    '& svg': { color: 'text.secondary', flex: 'none' },
    '&:hover': {
      bgcolor: accentTint(theme, 'hover'),
      color: accentOf(theme),
      '& svg': { color: accentOf(theme) }
    },
    '&.Mui-focusVisible': { bgcolor: accentTint(theme, 'selected') },
    // THE CHOSEN ROW OF A SELECT. MUI paints `Mui-selected` from
    // `palette.primary`, which until phase 0 lands is still the Minimal
    // green (DESIGN.md §4, §27): the chosen row is the accent tint, with
    // the word and the glyph in the accent, like everything else that is
    // "you are here".
    '&.Mui-selected': {
      bgcolor: accentTint(theme, 'selected'),
      color: accentOf(theme),
      fontWeight: 600,
      '& svg': { color: accentOf(theme) },
      '&:hover': { bgcolor: accentTint(theme, 'soft') },
      '&.Mui-focusVisible': { bgcolor: accentTint(theme, 'soft') }
    }
  },
  // The same paper serves an autocomplete's popup, whose rows are options and
  // not menu items (DESIGN.md §18: "the popup is stratum 3, rows 36px, `data`
  // size"). The recipe matches a migrated list's `layerSx`, so a picker in a
  // form and a picker in a filter bar open the same thing.
  '& .MuiAutocomplete-listbox': { p: 0 },
  // The listbox is named in the selector too: the portal's MuiAutocomplete
  // override styles the option from inside `.MuiAutocomplete-listbox` (it
  // gives each one `margin: 8px 0`), and a rule on the option alone is the
  // less specific of the two.
  '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
    my: 0,
    minHeight: 36,
    px: '8px !important',
    py: 0.5,
    gap: 1,
    borderRadius: `${RADIUS.control}px`,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    fontWeight: 500,
    color: 'text.primary',
    '&.Mui-focused': { bgcolor: `${accentTint(theme, 'hover')} !important` },
    '&[aria-selected="true"]': {
      bgcolor: `${accentTint(theme, 'selected')} !important`
    }
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    color: 'text.secondary'
  }
});

/**
 * THE SCRIM BEHIND A FLOATING LAYER (DESIGN.md §8, stratum 4):
 * `rgba(6,23,54,.56)` — an even navy wash, the value the stratum table names.
 *
 * The template's `src/theme/overrides/Backdrop.js` draws a 75° gradient from
 * `grey.900` at 48% to `grey.900` at **1**, so the far corner behind every
 * dialog is OPAQUE: a black wedge across one side of the page, which reads as
 * a rendering fault rather than as depth. Found by the sessions building the
 * expense record and the create form, 08/10/2026, behind the settle dialog.
 *
 * This is a page-scoped rule, mounted through `GlobalStyles` with the page and
 * removed with it — the same mechanism `LAYER_FONT_STYLES` uses for the UI
 * face, and for the same reason: a backdrop renders in a portal, outside the
 * page's subtree, where a page-scoped `sx` cannot reach it. Scoping it this way
 * leaves the ~40 dialogs on unmigrated pages exactly as they are.
 *
 * Fixing the override itself is the right end state and is a DECISION for the
 * owner, not a patch: it changes every dialog, drawer and menu in the portal at
 * once.
 */
export const LAYER_SCRIM_STYLES = {
  // `MuiBackdrop-invisible` is the one a Menu or a Popover mounts: it must stay
  // transparent, or every dropdown would dim the page behind it.
  '.MuiBackdrop-root:not(.MuiBackdrop-invisible)': {
    background: 'rgba(6, 23, 54, 0.56)'
  }
};

// A switch needs no sx: every MUI `Switch` is the theme's iOS switch
// (`src/theme/overrides/Switch.js`, DESIGN.md §18).
